'use client';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFire, FaTimes, FaCheck, FaLock } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

export default function DailyStreak() {
  const { user, updateUser } = useAuth();
  const [open, setOpen]       = useState(false);
  const [streak, setStreak]   = useState(null);
  const [quests, setQuests]   = useState([]);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    const todayKey = new Date().toLocaleDateString('fr-CA'); // format YYYY-MM-DD local

    const doCheckIn = async () => {
      // Garde localStorage : on ne rappelle pas l'API si déjà fait aujourd'hui
      const lastLocal = localStorage.getItem('qfl_streak_date');
      if (lastLocal === todayKey) {
        setStreak(user?.streak_days ?? 0);
        return;
      }

      try {
        const { data } = await api.post('/streak/checkin');
        setStreak(data.streakDays);
        localStorage.setItem('qfl_streak_date', todayKey);

        if (!data.alreadyCheckedIn) {
          setChecked(true);
          setOpen(true);
          if (data.coinsEarned) {
            toast.success(`+${data.coinsEarned} pièces — Streak jour ${data.streakDays} ! 🔥`);
            updateUser({ ...user, coins: (user?.coins || 0) + data.coinsEarned });
          }
        }
      } catch { /* silencieux */ }
    };

    const loadQuests = async () => {
      try {
        const { data } = await api.get('/streak/quests');
        setQuests(data.quests || []);
      } catch { /* silencieux */ }
    };

    doCheckIn();
    loadQuests();
  }, []);

  const handleClaim = async (questType) => {
    try {
      const { data } = await api.post(`/streak/quests/${questType}/claim`);
      toast.success(`+${data.coinsEarned} pièces réclamées !`);
      updateUser({ ...user, coins: data.newCoins });
      setQuests((q) => q.map((x) => x.quest_type === questType ? { ...x, claimed: true } : x));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    }
  };

  const streakDisplay = streak ?? user?.streak_days ?? 0;

  return (
    <>
      {/* Bouton flottant streak */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-bold transition-all"
        style={{
          background: streakDisplay > 0 ? 'rgba(249,115,22,0.2)' : 'rgba(255,255,255,0.07)',
          border: streakDisplay > 0 ? '1px solid rgba(249,115,22,0.4)' : '1px solid rgba(255,255,255,0.1)',
          fontFamily: "'Rajdhani', sans-serif",
          color: streakDisplay > 0 ? '#f97316' : 'rgba(255,255,255,0.5)',
        }}
      >
        <FaFire size={13} />
        <span>{streakDisplay}j</span>
      </button>

      {/* Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-end justify-center p-4 pb-24"
            style={{ background: 'rgba(0,0,0,0.82)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              className="w-full max-w-sm rounded-3xl p-6 space-y-5"
              style={{
                background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 100%)',
                border: '1.5px solid rgba(240,180,41,0.5)',
                boxShadow: '0 -8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(240,180,41,0.1)',
              }}
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header streak */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaFire size={22} className="text-orange-400" />
                  <div>
                    <p className="font-black text-white text-lg" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      STREAK QUOTIDIEN
                    </p>
                    <p className="text-orange-400 text-xs font-bold">{streakDisplay} jour{streakDisplay > 1 ? 's' : ''} consécutif{streakDisplay > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white">
                  <FaTimes size={16} />
                </button>
              </div>

              {/* Jalons streak */}
              <div className="flex justify-between gap-1">
                {[1, 2, 3, 5, 7, 14, 30].map((day) => (
                  <div
                    key={day}
                    className="flex flex-col items-center gap-1"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold"
                      style={{
                        background: streakDisplay >= day ? 'rgba(249,115,22,0.3)' : 'rgba(255,255,255,0.05)',
                        border: streakDisplay >= day ? '1px solid rgba(249,115,22,0.5)' : '1px solid rgba(255,255,255,0.1)',
                        color: streakDisplay >= day ? '#f97316' : 'rgba(255,255,255,0.3)',
                      }}
                    >
                      {streakDisplay >= day ? <FaFire size={12} /> : day}
                    </div>
                    <span className="text-[9px] text-white/30" style={{ fontFamily: "'Rajdhani', sans-serif" }}>J{day}</span>
                  </div>
                ))}
              </div>

              {/* Quêtes du jour */}
              {quests.length > 0 && (
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-widest mb-2" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    QUÊTES DU JOUR
                  </p>
                  <div className="space-y-2">
                    {quests.map((q) => (
                      <div
                        key={q.quest_type}
                        className="flex items-center gap-3 rounded-xl p-3"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}
                      >
                        <div className="flex-1">
                          <p className="text-white text-sm font-semibold">{q.label}</p>
                          <div className="mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-qfl-gold transition-all"
                              style={{ width: `${Math.min((q.progress / q.target) * 100, 100)}%` }}
                            />
                          </div>
                          <p className="text-white/40 text-xs mt-0.5">{Math.min(q.progress, q.target)}/{q.target}</p>
                        </div>
                        <div className="flex-shrink-0">
                          {q.claimed ? (
                            <span className="text-qfl-gold"><FaCheck size={16} /></span>
                          ) : q.completed ? (
                            <button
                              onClick={() => handleClaim(q.quest_type)}
                              className="text-xs font-bold bg-qfl-gold text-black px-2 py-1 rounded-lg"
                              style={{ fontFamily: "'Rajdhani', sans-serif" }}
                            >
                              +{q.reward_coins}
                            </button>
                          ) : (
                            <span className="text-white/20 text-xs font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                              +{q.reward_coins}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
